import { Link } from "react-router-dom";
import { Quote, FileText, FileDown, ExternalLink } from "lucide-react";
import Reveal from "@/components/Reveal";
import Eyebrow from "@/components/Eyebrow";
import LoopDetailSection from "@/components/LoopDetailSection";
import CircularLoop from "@/components/CircularLoop";
import { loops } from "@/data/loops";

const heroImg =
  "https://images.unsplash.com/photo-1776918570415-52e7f702802a?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";
const usmImg =
  "https://images.unsplash.com/photo-1755606045126-1aeb4f0e7c2d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const upmImg =
  "https://images.unsplash.com/photo-1763844599737-be3850c60cea?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

const sdgs = [
  { code: "SDG 1", title: "No Poverty", body: "Asnaf families generate income through the LKTN buyback model." },
  { code: "SDG 2", title: "Zero Hunger", body: "Affordable, accessible nutrition through Cendawan Hub." },
  { code: "SDG 8", title: "Decent Work", body: "Fair-price buyback for asnaf grower families. Production roles for disabled and elderly workers." },
  { code: "SDG 12", title: "Responsible Consumption", body: "Kenaf-based beglog reduces deforestation pressure." },
  { code: "SDG 13", title: "Climate Action", body: "Circular waste-to-compost model reduces agricultural emissions." },
  { code: "SDG 17", title: "Partnerships", body: "LKTN, USM, UPM collaborations as core operating principle." },
];

const Impact = () => {
  return (
    <div data-testid="impact-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Sustainable farmland aerial view" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
        </div>
        <div className="relative container-x pb-20 md:pb-28 pt-32">
          <div className="max-w-3xl">
            <Eyebrow color="ochre" className="mb-8">
              <span className="text-ecospora-ochre">Impact · By Design</span>
            </Eyebrow>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[72px] font-bold leading-[1.05] tracking-tight text-ecospora-cream text-balance">
              A model that gives back <span className="italic font-light text-ecospora-green-soft">more than it takes.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ecospora-cream/85 max-w-2xl leading-relaxed">
              At Ecospora, impact is the design. Five circular loops, running across every level of our operation — from Perlis outward.
            </p>
          </div>
        </div>
      </section>

      {/* OPERATING PRINCIPLE */}
      <section className="relative section-y bg-ecospora-cream overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-ecospora-green-soft/30 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -left-32 w-[460px] h-[460px] rounded-full bg-ecospora-ochre/15 blur-3xl"
        />

        <div className="relative container-x">
          <Reveal className="max-w-4xl">
            <div className="flex items-center gap-4 mb-10">
              <span className="text-[11px] font-semibold tracking-[0.32em] uppercase text-ecospora-green-mid">
                Our Operating Principle
              </span>
              <span aria-hidden="true" className="h-px flex-1 bg-ecospora-green-deep/20 max-w-[120px]" />
            </div>

            <h2 className="font-display text-5xl md:text-6xl lg:text-[80px] font-bold tracking-tight text-ecospora-green-deep leading-[0.98]">
              Nothing leaves
              <br />
              <span className="italic font-light text-ecospora-green-mid">the system.</span>
            </h2>
          </Reveal>

          <Reveal className="mt-16 md:mt-20 grid md:grid-cols-12 gap-10 md:gap-12 items-start">
            <div className="md:col-span-7">
              <p className="font-display text-2xl md:text-3xl text-ecospora-green-deep leading-[1.3]">
                Most companies run a <span className="italic font-light">sustainability programme.</span>
                <br />
                We run a sustainability <span className="not-italic font-semibold">operating system.</span>
              </p>
            </div>
            <div className="md:col-span-5 md:pt-3">
              <div className="h-px w-12 bg-ecospora-ochre mb-6 md:hidden" />
              <p className="text-[17px] text-ecospora-charcoal/80 leading-[1.75]">
                Ecospora is built around five circular loops that touch every stage of how we operate. How we grow. How we partner. How we sell. How we return what's left to the soil. Together, they form the system that lets us promise <strong className="text-ecospora-green-deep">zero waste by design</strong>, and actually mean it.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-20 md:mt-24">
            <div
              data-testid="loops-counter"
              className="rounded-2xl bg-ecospora-white border border-ecospora-green-deep/10 overflow-hidden"
            >
              <div className="grid grid-cols-3 divide-x divide-ecospora-green-deep/10">
                <CounterTile number="05" label="Loops in the system" caption="The full operating principle" />
                <CounterTile number="03" label="Running today" caption="Supply · Community · Commercial" />
                <CounterTile number="02" label="Building with universities" caption="Soil with UPM · Material with USM" />
              </div>
            </div>
            <p className="mt-6 text-center text-[12px] tracking-[0.22em] uppercase text-ecospora-green-mid">
              Each loop is laid out below ↓
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE FIVE LOOPS — DETAIL SECTIONS */}
      <div data-testid="loop-detail-stack">
        {loops.map((loop, i) => (
          <LoopDetailSection
            key={loop.id}
            loop={loop}
            index={i}
            visual={loop.id === "loop-02" ? <CircularLoop /> : undefined}
          />
        ))}
      </div>

      {/* ESG / SDG */}
      <section className="section-y bg-ecospora-white">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-16">
            <Eyebrow className="mb-6">ESG, Mapped</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.1]">
              ESG is the model. <span className="italic font-light">Built into every loop.</span>
            </h2>
            <p className="mt-6 text-lg text-ecospora-charcoal/75 leading-relaxed">
              Our work directly contributes to the United Nations Sustainable Development Goals. We don't claim credit lightly — every commitment below is structurally embedded in how we operate.
            </p>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-5">
            {sdgs.map((sdg, i) => (
              <div
                key={sdg.code}
                data-testid={`sdg-${i}`}
                className="p-8 rounded-2xl border border-ecospora-green-deep/15 hover:border-ecospora-green-deep/40 transition-colors"
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-ecospora-ochre">
                    {sdg.code}
                  </span>
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight text-ecospora-green-deep">
                  {sdg.title}
                </h3>
                <p className="mt-4 text-ecospora-charcoal/75 leading-relaxed text-[15px]">
                  {sdg.body}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* INNOVATION IN SERVICE OF IMPACT (reframed) */}
      <section className="section-y bg-ecospora-cream">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-20">
            <Eyebrow className="mb-6">The Loops We're Building</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.1]">
              Research, <span className="italic font-light">in service of the system.</span>
            </h2>
            <p className="mt-6 text-lg text-ecospora-charcoal/80 leading-[1.7]">
              Loops 04 and 05 are not yet at scale — they're being engineered with academic rigour, in partnership with two of Malaysia's leading universities. Here's what's coming.
            </p>
          </Reveal>

          <Reveal className="grid md:grid-cols-12 gap-10 items-center mb-24">
            <div className="md:col-span-6 aspect-[4/3] rounded-2xl overflow-hidden">
              <img src={usmImg} alt="USM mycelium packaging research" className="w-full h-full object-cover" />
            </div>
            <div className="md:col-span-6">
              <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-ecospora-ochre">
                Loop 05 — The Material Loop with USM
              </span>
              <h3 className="font-display text-3xl md:text-4xl font-bold text-ecospora-green-deep mt-3 leading-tight tracking-tight">
                USM × Ecospora
              </h3>
              <p className="mt-5 text-lg text-ecospora-charcoal/80 leading-[1.7]">
                Single-use plastic is choking Malaysia's waterways. With USM's mycology lab, we're growing packaging from mushroom mycelium — fully biodegradable, locally manufacturable, and competitive in cost. Pilot production targeted for Q4 2026.
              </p>
              <Link to="/impact#loop-05" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold uppercase tracking-[0.08em] text-ecospora-green-deep border-b border-ecospora-ochre pb-1 hover:gap-3 transition-all">
                Read the loop
              </Link>
            </div>
          </Reveal>

          <Reveal className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-6 md:order-2 aspect-[4/3] rounded-2xl overflow-hidden">
              <img src={upmImg} alt="UPM composting research" className="w-full h-full object-cover" />
            </div>
            <div className="md:col-span-6 md:order-1">
              <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-ecospora-ochre">
                Loop 04 — The Soil Loop with UPM
              </span>
              <h3 className="font-display text-3xl md:text-4xl font-bold text-ecospora-green-deep mt-3 leading-tight tracking-tight">
                UPM × Ecospora
              </h3>
              <p className="mt-5 text-lg text-ecospora-charcoal/80 leading-[1.7]">
                Spent mushroom substrate — the leftover material after harvest — is typically waste. With UPM, we've developed a composting process that converts it into agricultural-grade fertiliser, which goes back to kenaf farms. The loop closes.
              </p>
              <Link to="/impact#loop-04" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold uppercase tracking-[0.08em] text-ecospora-green-deep border-b border-ecospora-ochre pb-1 hover:gap-3 transition-all">
                Read the loop
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS / RESERVED SPACE */}
      <section className="section-y bg-ecospora-white">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-16">
            <Eyebrow className="mb-6">Stories from the Field</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.1]">
              The model, <span className="italic font-light">in their words.</span>
            </h2>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-6">
            {["Asnaf grower, Kelantan", "LKTN Programme Lead", "USM / UPM researcher"].map((role, i) => (
              <div
                key={role}
                data-testid={`testimonial-reserved-${i}`}
                className="relative p-10 rounded-2xl bg-ecospora-cream border border-dashed border-ecospora-green-deep/30"
              >
                <Quote size={56} strokeWidth={0.75} className="text-ecospora-green-soft" />
                <p className="font-display text-xl italic text-ecospora-green-deep mt-4 leading-snug">
                  Space reserved for our partners.
                </p>
                <p className="mt-3 text-sm text-ecospora-charcoal/65 leading-relaxed">
                  Real story arriving as we capture quotes from the field.
                </p>
                <div className="mt-8 pt-6 border-t border-ecospora-green-deep/15 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.18em] text-ecospora-charcoal/55">
                    {role}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-ecospora-ochre">
                    Coming soon
                  </span>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* REPORTS */}
      <section className="section-y bg-ecospora-green-soft">
        <div className="container-x">
          <Reveal className="text-center mb-14">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep">
              Read the full picture.
            </h2>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: "Ecospora Impact Report", cta: "Download PDF" },
              { icon: FileText, title: "ESG Commitments", cta: "Download PDF" },
              { icon: ExternalLink, title: "Partnership case studies", cta: "View" },
            ].map((r, i) => (
              <a
                key={r.title}
                href="#"
                data-testid={`resource-${i}`}
                className="group flex flex-col p-10 rounded-2xl bg-ecospora-cream border border-ecospora-green-deep/15 hover:bg-ecospora-white transition-all hover:-translate-y-1"
              >
                <r.icon size={28} strokeWidth={1.5} className="text-ecospora-green-deep" />
                <h3 className="font-display text-2xl font-semibold tracking-tight text-ecospora-green-deep mt-6">
                  {r.title}
                </h3>
                <span className="inline-flex items-center gap-2 mt-auto pt-8 text-sm font-semibold uppercase tracking-[0.08em] text-ecospora-green-deep group-hover:gap-3 transition-all">
                  <FileDown size={16} strokeWidth={1.5} /> {r.cta}
                </span>
              </a>
            ))}
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-ecospora-green-deep py-24 md:py-32">
        <div className="container-x">
          <Reveal className="max-w-3xl">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ecospora-cream leading-[1.1] text-balance">
              There's a place for you <span className="italic font-light text-ecospora-green-soft">in this loop.</span>
            </h2>
            <p className="mt-6 text-lg text-ecospora-cream/80 max-w-2xl leading-relaxed">
              Whether you're a buyer, a partner, or a researcher, the model grows when more hands join it.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/collaborate" className="btn-ochre" data-testid="impact-cta-partner">Partner with us</Link>
              <a href="#" className="inline-flex items-center justify-center gap-2 border border-ecospora-cream/40 text-ecospora-cream uppercase text-sm font-semibold tracking-[0.08em] px-8 py-4 rounded-full hover:bg-ecospora-cream hover:text-ecospora-green-deep transition" data-testid="impact-cta-stock">
                Stock our products
              </a>
              <Link to="/contact#form" className="inline-flex items-center justify-center gap-2 text-ecospora-cream uppercase text-sm font-semibold tracking-[0.08em] px-2 py-4 hover:text-ecospora-ochre transition" data-testid="impact-cta-team">
                Talk to our team →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const CounterTile = ({ number, label, caption }) => (
  <div className="p-6 md:p-8 lg:p-10 text-center md:text-left">
    <div className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-ecospora-green-deep tracking-tight leading-none">
      {number}
    </div>
    <div className="mt-4 text-[11px] md:text-xs font-semibold tracking-[0.22em] uppercase text-ecospora-ochre">
      {label}
    </div>
    <div className="mt-2 text-[13px] md:text-sm text-ecospora-charcoal/60 leading-snug">
      {caption}
    </div>
  </div>
);

export default Impact;
